import { useTranslation } from "@thyra/landing-i18n";
import { Badge } from "@thyra/ui/badge";
import { Button } from "@thyra/ui/button";
import { Checkbox } from "@thyra/ui/checkbox";
import {
  Bell,
  Bot,
  Check,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  Layers,
  LayoutDashboard,
  Loader2,
  type LucideIcon,
  MessageSquare,
  PanelLeft,
  Search,
  SlidersHorizontal,
  Wrench,
  X,
} from "lucide-react";

/**
 * The web app's Approvals page (app.thyra-ai.com/approvals), rebuilt from the
 * real thing rather than drawn: the primitives are @thyra/ui's own Badge,
 * Button and Checkbox, and the markup around them follows the app's sources
 * class for class. Keep it in step with:
 *
 *   shell + top nav  apps/web/components/layout/shell-frame.tsx, top-nav.tsx
 *   sidebar rows     apps/web/config/sidebar-sections.ts, packages/ui/src/sidebar.tsx
 *   page header      apps/web/components/layout/page-header.tsx
 *   queue rows       apps/web/components/work-queue/work-queue-row-inline.tsx,
 *                    work-queue-row-actions.tsx, apps/web/lib/work-queue-visual.ts
 *
 * One deliberate liberty: a row that goes through a connection leads with
 * that connection's brand logo (the same /connections/*.svg the app uses in
 * ConnectionProviderIcon) where the app's row shows its per-kind glyph.
 */

export type ShowcasePhase = "pending" | "approving" | "done";

type QueueRow = {
  id: string;
  Icon: LucideIcon;
  iconClassName: string;
  shellClassName: string;
  /** Brand logo of the connection the request goes through, when it has one. */
  logo?: { src: string; bleed?: boolean };
  kind: "tool" | "approval" | "question";
  /** Key of the row's copy under `home:heroPreview.rows`. */
  copy: "sendEmail" | "calendar" | "linear" | "refund";
  itemCount?: number;
  agent: string;
  intent: "decide" | "answer";
};

/** The row the phone approves; it leaves the pending queue once that lands. */
const FEATURED_ROW_ID = "send-email";

const QUEUE_ROWS: QueueRow[] = [
  {
    id: FEATURED_ROW_ID,
    Icon: Wrench,
    iconClassName: "text-chart-1",
    shellClassName: "border-chart-1/20 bg-chart-1/10",
    logo: { src: "/connections/gmail.svg" },
    kind: "tool",
    copy: "sendEmail",
    agent: "inbox-helper",
    intent: "decide",
  },
  {
    id: "calendar-invites",
    Icon: ClipboardCheck,
    iconClassName: "text-chart-2",
    shellClassName: "border-chart-2/20 bg-chart-2/10",
    logo: { src: "/connections/googlecalendar.svg" },
    kind: "approval",
    copy: "calendar",
    itemCount: 3,
    agent: "scheduler",
    intent: "decide",
  },
  {
    id: "linear-issue",
    Icon: Wrench,
    iconClassName: "text-chart-1",
    shellClassName: "border-chart-1/20 bg-chart-1/10",
    // Linear's mark ships with its own filled square, so it fills the tile.
    logo: { src: "/connections/linear.svg", bleed: true },
    kind: "tool",
    copy: "linear",
    agent: "support-desk",
    intent: "decide",
  },
  {
    id: "refund-question",
    Icon: CircleHelp,
    iconClassName: "text-warning",
    shellClassName: "border-warning/20 bg-warning/10",
    kind: "question",
    copy: "refund",
    agent: "support-desk",
    intent: "answer",
  },
];

const ROW_GRID =
  "grid grid-cols-[2.5rem_minmax(0,1fr)_4.75rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_4.5rem_4.75rem] xl:grid-cols-[2.5rem_minmax(0,1fr)_4.5rem_8rem_4.75rem]";

const ICON_BUTTON_CLASS =
  "group size-8 shrink-0 text-muted-foreground hover:text-foreground";

const SIDEBAR_ITEMS: {
  Icon: LucideIcon;
  id: "chat" | "configure" | "agents" | "patterns" | "approvals";
  active?: boolean;
}[] = [
  { Icon: MessageSquare, id: "chat" },
  { Icon: SlidersHorizontal, id: "configure" },
  { Icon: Bot, id: "agents" },
  { Icon: Layers, id: "patterns" },
  { Icon: ClipboardList, id: "approvals", active: true },
];

const KIND_KEYS = {
  tool: "kindTool",
  approval: "kindApproval",
  question: "kindQuestion",
} as const;

function TopNav() {
  const { t } = useTranslation("home");
  const itemClass =
    "inline-flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground";
  return (
    <header className="shrink-0 border-b border-border bg-shell-header px-3 py-2 sm:px-4">
      <div className="flex h-12 items-center gap-3 rounded-2xl bg-shell-topnav px-3">
        <img
          src="/icon.svg"
          alt=""
          width={18}
          height={18}
          className="shrink-0 object-contain"
        />
        <span className="h-5 w-px shrink-0 bg-border" />
        <nav className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
          <span className={itemClass}>
            <Search className="size-4 shrink-0" />
            <span className="hidden sm:inline">
              {t("heroPreview.nav.search")}
            </span>
            <kbd className="hidden h-5 items-center gap-0.5 rounded-md border border-border/80 bg-background/70 px-1.5 font-mono text-[10px] font-medium text-muted-foreground shadow-sm sm:flex">
              <span className="text-xs">⌘</span>K
            </kbd>
          </span>
          <span className={itemClass}>
            <MessageSquare className="size-4 shrink-0" />
            {t("heroPreview.nav.chat")}
          </span>
          <span
            className={`${itemClass} bg-shell-topnav-active text-foreground shadow-sm`}
          >
            <LayoutDashboard className="size-4 shrink-0" />
            {t("heroPreview.nav.workspace")}
          </span>
        </nav>
      </div>
    </header>
  );
}

function Sidebar() {
  const { t } = useTranslation("home");
  return (
    <aside className="hidden w-52 shrink-0 flex-col bg-sidebar text-sidebar-foreground lg:flex">
      <div className="px-2 pb-2">
        <div className="flex h-12 w-full items-center gap-2 rounded-md p-2 text-left text-sm">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Bot className="size-4" />
          </span>
          <span className="grid min-w-0 flex-1 leading-tight">
            <span className="truncate font-semibold">Inbox Helper</span>
            <span className="truncate text-xs text-sidebar-foreground/70">
              {t("heroPreview.sidebar.role")}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-sidebar-foreground/60" />
        </div>
      </div>
      <div className="flex min-w-0 flex-col p-2">
        <div className="flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70">
          {t("heroPreview.sidebar.workspace")}
        </div>
        <ul className="flex w-full min-w-0 flex-col gap-1">
          {SIDEBAR_ITEMS.map(({ Icon, id, active }) => (
            <li key={id}>
              <span
                data-active={active ? "true" : undefined}
                className="flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:shadow-sm"
              >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">
                  {t(`heroPreview.sidebar.${id}`)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function RowActions({ row, busy }: { row: QueueRow; busy: boolean }) {
  const { t } = useTranslation("home");
  if (row.intent === "answer") {
    return (
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={ICON_BUTTON_CLASS}
          aria-label={t("heroPreview.web.answer")}
        >
          <MessageSquare className="size-4" aria-hidden />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={ICON_BUTTON_CLASS}
        aria-label={t("heroPreview.web.approve")}
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Check
            className="size-4 transition-colors group-hover:text-success"
            aria-hidden
          />
        )}
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={ICON_BUTTON_CLASS}
        aria-label={t("heroPreview.web.reject")}
      >
        <X
          className="size-4 transition-colors group-hover:text-destructive"
          aria-hidden
        />
      </Button>
    </div>
  );
}

/**
 * `ghost` renders an invisible copy of a row that expands exactly as the real
 * one collapses, so the window keeps its height while the featured row leaves
 * the queue and the page never shifts under the reader.
 */
function QueueRowView({
  row,
  phase,
  ghost = false,
}: {
  row: QueueRow;
  phase: ShowcasePhase;
  ghost?: boolean;
}) {
  const { t } = useTranslation("home");
  const featured = row.id === FEATURED_ROW_ID;
  const gone = (featured && phase === "done") !== ghost;
  const { Icon } = row;
  const title = t(`heroPreview.rows.${row.copy}.title`);

  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
        gone ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100"
      } ${ghost ? "invisible" : ""}`}
      aria-hidden={ghost || undefined}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={`${ROW_GRID} items-start border-b border-border py-2.5`}
        >
          <div className="flex justify-center px-2 pt-2">
            <Checkbox
              aria-label={t("heroPreview.web.selectRow", { title })}
              checked={featured && phase !== "pending"}
            />
          </div>

          <div className="flex min-w-0 items-start gap-2 px-1 py-0.5 sm:px-3">
            <span className="mt-0.5 hidden size-7 shrink-0 items-center justify-center text-muted-foreground sm:flex">
              <ChevronRight className="size-4" aria-hidden />
            </span>
            {row.logo ? (
              <div
                className="mt-0.5 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-white"
                aria-hidden
              >
                <img
                  src={row.logo.src}
                  alt=""
                  className={`object-contain ${row.logo.bleed ? "size-full" : "size-[18px]"}`}
                />
              </div>
            ) : (
              <div
                className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border ${row.shellClassName}`}
                aria-hidden
              >
                <Icon className={`size-4 ${row.iconClassName}`} />
              </div>
            )}
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-sm font-medium text-foreground">
                {title}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {t(`heroPreview.rows.${row.copy}.detail`)}
              </p>
              <div className="flex min-w-0 items-center gap-1.5 pt-0.5">
                <Badge
                  variant="outline"
                  className="shrink-0 text-[11px] font-normal"
                >
                  {t(`heroPreview.web.${KIND_KEYS[row.kind]}`)}
                </Badge>
                {row.itemCount ? (
                  <Badge
                    variant="secondary"
                    className="shrink-0 text-[11px] font-normal"
                  >
                    {t("heroPreview.web.items", { count: row.itemCount })}
                  </Badge>
                ) : null}
                <span className="truncate text-[11px] text-muted-foreground/80">
                  {t("heroPreview.web.agent", { name: row.agent })}
                </span>
              </div>
            </div>
          </div>

          <span className="hidden whitespace-nowrap px-2 pt-1.5 text-sm text-muted-foreground sm:block">
            {t(
              row.intent === "decide"
                ? "heroPreview.web.decide"
                : "heroPreview.web.answerIntent",
            )}
          </span>
          <span className="hidden whitespace-nowrap px-2 pt-1.5 text-sm text-muted-foreground xl:block">
            {t(`heroPreview.rows.${row.copy}.when`)}
          </span>
          <div className="px-2">
            <RowActions row={row} busy={featured && phase === "approving"} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function WebAppPreview({ phase }: { phase: ShowcasePhase }) {
  const { t } = useTranslation("home");
  const pendingCount =
    phase === "done" ? QUEUE_ROWS.length - 1 : QUEUE_ROWS.length;

  return (
    <div className="app-surface flex flex-col bg-background text-left text-foreground">
      <TopNav />
      <div className="flex min-h-0 flex-1 bg-sidebar">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col bg-background lg:rounded-tl-xl">
          <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border/70 px-4">
            <PanelLeft className="size-4 shrink-0 text-muted-foreground" />
            <span className="h-4 w-px shrink-0 bg-border/70" />
            <span className="inline-flex min-w-0 items-center gap-1.5 px-1.5 py-1 text-sm font-medium text-foreground">
              <ClipboardList className="size-3.5 shrink-0" />
              <span className="truncate">{t("heroPreview.web.title")}</span>
            </span>
            <span className="min-w-0 flex-1" />
            <span className="relative flex size-8 items-center justify-center text-muted-foreground">
              <Bell className="size-4" />
              <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-primary" />
            </span>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
            <div className="-mx-4 border-b border-border/70 px-4 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/45 text-muted-foreground">
                    <ClipboardList className="size-5" />
                  </div>
                  <div className="min-w-0">
                    {/* The app renders this as its page h1; here it is copy
                        inside an illustration, so it stays out of the
                        landing's heading outline. */}
                    <p className="truncate text-xl font-semibold tracking-tight text-foreground">
                      {t("heroPreview.web.title")}
                    </p>
                    <p className="mt-1 truncate text-sm leading-6 text-muted-foreground">
                      {t("heroPreview.web.description")}
                    </p>
                  </div>
                </div>
                <Badge variant="secondary">{pendingCount}</Badge>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <div
                className={`${ROW_GRID} h-10 items-center border-b border-border text-sm font-medium text-muted-foreground`}
              >
                <div className="flex justify-center px-2">
                  <Checkbox aria-label={t("heroPreview.web.selectAll")} />
                </div>
                <span className="px-1 sm:px-3">
                  {t("heroPreview.web.colRequest")}
                </span>
                <span className="hidden px-2 sm:block">
                  {t("heroPreview.web.colType")}
                </span>
                <span className="hidden px-2 xl:block">
                  {t("heroPreview.web.colWhen")}
                </span>
                <span />
              </div>
              {QUEUE_ROWS.map((row) => (
                <QueueRowView key={row.id} row={row} phase={phase} />
              ))}
              <QueueRowView row={QUEUE_ROWS[0]!} phase={phase} ghost />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
